import { MDBBtn, MDBCheckbox, MDBIcon, MDBInput, MDBTypography, MDBValidationItem } from "mdb-react-ui-kit"
import React, { useEffect, useState } from 'react'
import { isoToGermanDate } from "../../../../utils/dateTools"
import { useTranslation } from "react-i18next"
import { generateClient } from 'aws-amplify/api';
import useDebounce from "../../../../utils/debounce";

const INVITATION_CODE_WITH_DISCOUNT_QUERY = /* GraphQL */ `
  query InvitationCodeByCode($code: String!) {
    invitationCodeByCode(code: $code) {
      items {
        id
        code
        discountCode {
          id
          code
          name {
            language
            text
          }
          discountType
          discountPercentage
          discountAmount
          maxUsage
          usageCount
          startDate
          endDate
          status
          restrictedProductIds
          canBeCombined
        }
      }
    }
  }
`;

export default function Visit({params, setParams, eventDays, event, hasInvitationCodes, invitationCodeRequired, suggestedInvitationCode, bookingSelectDays, handleClickPrev, handleClickNext, validations, showValidations, setShowValidations, setLinkedDiscount}) {
    const { t, i18n } = useTranslation()
    const client = generateClient()
    const [discountInfoMessage, setDiscountInfoMessage] = useState(null);

    const checkInvitationCodeDiscount = async (code) => {
        if (!code?.trim()) {
            setLinkedDiscount(null);
            setDiscountInfoMessage(null);
            return;
        }
        try {
            const response = await client.graphql({
                query: INVITATION_CODE_WITH_DISCOUNT_QUERY,
                variables: { code: code.trim() }
            });
            const invitationCode = response.data.invitationCodeByCode.items?.find(
                ic => ic.code === code.trim() && (!event?.id || true)
            );
            const discount = invitationCode?.discountCode;
            if (discount) {
                // Validate the linked discount
                if (discount.status !== "active") {
                    setLinkedDiscount(null);
                    setDiscountInfoMessage(null);
                    return;
                }
                if (discount.startDate && new Date(discount.startDate) > new Date()) {
                    setLinkedDiscount(null);
                    setDiscountInfoMessage(null);
                    return;
                }
                if (discount.endDate && new Date(discount.endDate) < new Date()) {
                    setLinkedDiscount(null);
                    setDiscountInfoMessage(null);
                    return;
                }
                if (discount.maxUsage && discount.usageCount >= discount.maxUsage) {
                    setLinkedDiscount(null);
                    setDiscountInfoMessage({ type: "warning", text: t("The free ticket quota for this invitation code has been reached.") });
                    return;
                }
                // Discount is valid - pass it up
                setLinkedDiscount({ ...discount, isFromInvitation: true });
                const discountLabel = discount.discountPercentage === 100
                    ? t("Free ticket")
                    : discount.discountPercentage
                        ? `${discount.discountPercentage}% ${t("Discount")}`
                        : `${discount.discountAmount}€ ${t("Discount")}`;
                setDiscountInfoMessage({ type: "success", text: `${discountLabel} — ${t("Discount applied successfully")}` });
            } else {
                setLinkedDiscount(null);
                setDiscountInfoMessage(null);
            }
        } catch (error) {
            console.error("Error checking invitation code discount:", error);
            setLinkedDiscount(null);
            setDiscountInfoMessage(null);
        }
    };

    const scrollToFirstError = () => {
        setTimeout(() => {
            const firstInvalid = document.querySelector('.is-invalid');
            if (firstInvalid) {
                firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const handleClickButton = async () => {
        // Validate directly — don't rely on debounced state only
        const noDaysSelected = !params.selectedDays || params.selectedDays.length === 0;
        const noInvitationCode = invitationCodeRequired && !params.invitationCode?.trim();

        if(validations?.visit?.length > 0 || noDaysSelected || noInvitationCode) {
            setShowValidations(old => ({...old, visit: true}));
            scrollToFirstError();
            return;
        } else {
            setShowValidations(old => ({...old, visit: false}));
        }

        if(validations?.visit?.length === 0 && !noDaysSelected) {
            // Check for linked discount before proceeding
            if (hasInvitationCodes && params.invitationCode?.trim()) {
                await checkInvitationCodeDiscount(params.invitationCode);
            }
            handleClickNext();
        }

    }

    // Check for linked discount when invitation code changes (typed or pre-filled from URL)
    const debouncedInvitationCode = useDebounce(params.invitationCode, 1000);
    useEffect(() => {
        if (hasInvitationCodes && debouncedInvitationCode?.trim()) {
            checkInvitationCodeDiscount(debouncedInvitationCode);
        } else {
            setLinkedDiscount(null);
            setDiscountInfoMessage(null);
        }
    }, [debouncedInvitationCode]);

    // If event !bookingSelectDays , automatically select all days
    useEffect(() => {
        if(!bookingSelectDays) {
            setParams(old => ({...old, selectedDays: eventDays}));
        }
    }, [bookingSelectDays]);

  return (
<>
{hasInvitationCodes &&
<>
<MDBTypography tag='h2' className="mb-lg-3">{t("Invitation code")}{invitationCodeRequired && " *"}</MDBTypography>
{invitationCodeRequired ? (
    suggestedInvitationCode ?
    <p>
        {t("Enter your invitation code you received from a company or a partner. If you didn't receive an invitation code, you can use the code") + " " + suggestedInvitationCode + "."}
    </p>
    :
    <p>
        {t("An invitation code is required to book a ticket for this event. Please enter your invitation code you received from a company or a partner.")}
    </p>
) : (
    suggestedInvitationCode ?
    <p>
        {t("Enter your invitation code you received from a company or a partner. If you didn't receive an invitation code, you can use the code") + " " + suggestedInvitationCode + "."}
    </p>
    :
    <p>
        {t("Enter your invitation code you received from a company or a partner.")}
    </p>
)}
<div style={{maxWidth: "400px"}} className="mx-auto">
        <MDBInput className={showValidations?.visit && validations?.visit?.includes("invitationCode") ? "is-invalid" : ""} label={t("Invitation code") + (invitationCodeRequired ? " *" : "")} required={invitationCodeRequired} value={params.invitationCode || ""} name="invitationCode" onChange={e => {setParams(old => ({...old, [e.target.name]: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")}))}} />
        {discountInfoMessage && <small className={`d-block mt-1 ${discountInfoMessage.type === "success" ? "text-success" : "text-warning"}`}><MDBIcon fas icon={discountInfoMessage.type === "success" ? "check-circle" : "exclamation-triangle"} className="me-1" />{discountInfoMessage.text}</small>}
</div>
</>
}

<MDBTypography tag='h2' className="my-lg-4">{t("Days")}</MDBTypography>
<div className="text-start mx-auto" style={{width: "fit-content"}}>
{eventDays && eventDays.map((day) => {
return (
    <MDBCheckbox
    key={day}
    className={showValidations?.visit && (!params.selectedDays || params.selectedDays.length === 0) ? "is-invalid" : ""}
    label={isoToGermanDate(day)}
    disabled={!bookingSelectDays}
    id={day}
    checked={params.selectedDays?.includes(day)}
    onChange={e => {
        if(e.target.checked) {
        setParams(old => ({...old, selectedDays: [...old.selectedDays, day].filter(Boolean).sort()}))
        } else {
        setParams(old => ({...old, selectedDays: old.selectedDays.filter(d => d !== day).filter(Boolean)}))
        }
    }}
    />
)}
)}
</div>

{showValidations?.visit && validations?.visit?.length > 0 && (
    <div className="alert alert-danger mt-3 text-center">
        <MDBIcon fas icon="exclamation-triangle" className="me-2" />
        {t("Please fill out all required fields.")}
        <div className="mt-2 small">
            {validations.visit.map((field) => {
                const fieldLabels = {
                    invitationCode: t("Invitation code"),
                    selectedDays: t("Days"),
                };
                return <span key={field} className="d-block">{fieldLabels[field] || field}</span>;
            })}
        </div>
    </div>
)}

<div className="text-center mt-4">
    <MDBBtn color="primary" outline className="me-3" onClick={handleClickPrev}>{t("Back")}</MDBBtn>
    <MDBBtn color="primary" onClick={handleClickButton}>{t("Next")}</MDBBtn>
</div>
</>
  )
}
